import { RefObject, useEffect, useState } from "react";
import { PlayerRef } from "@remotion/player";

/**
 * Binds scroll position to the Remotion Player's playhead.
 *
 * On every scroll event (passive listener, rAF-throttled) it computes
 * progress = scrollY / (scrollHeight - innerHeight) and seeks the player
 * to the matching frame. Returns the current progress (0–1) so DOM
 * overlays can sync to the same playhead.
 */
const useScrollFrame = (
  playerRef: RefObject<PlayerRef>,
  durationInFrames: number
): number => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let rafId: number | null = null;
    let lastFrame = -1;

    const update = () => {
      rafId = null;
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      const nextProgress =
        scrollable <= 0 ? 0 : Math.min(1, Math.max(0, window.scrollY / scrollable));
      const frame = Math.round(nextProgress * (durationInFrames - 1));
      if (frame !== lastFrame) {
        lastFrame = frame;
        playerRef.current?.seekTo(frame);
        setProgress(nextProgress);
      }
    };

    const onScroll = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(update);
      }
    };

    // Seek immediately on mount (e.g. when returning mid-page).
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [playerRef, durationInFrames]);

  return progress;
};

export default useScrollFrame;
